/* global SideDrawer */

class CartDrawer extends SideDrawer {
  connectedCallback() {
    if (!this.boundCartDrawerEvents) {
      this.bindEvents();
      this.boundCartDrawerEvents = true;
    }
    this.normalizeCartState();
  }

  getFocusTarget() {
    return this.querySelector('.cart-drawer__header .js-close-drawer')
      || this.querySelector('.cart-drawer__empty-content .js-close-drawer')
      || this.querySelector('.js-close-drawer');
  }

  initCrossSells() {
    this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
  }

  setPanelVisibility(panel, shouldShow) {
    if (!panel) return;

    panel.hidden = !shouldShow;
    panel.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');

    if (panel.classList.contains('cart-drawer__content')) {
      panel.classList.toggle('cart-drawer__content--hidden', !shouldShow);
    }
    if (panel.classList.contains('cart-drawer__footer')) {
      panel.classList.toggle('cart-drawer__footer--hidden', !shouldShow);
    }
    if (panel.classList.contains('cart-drawer__empty-content')) {
      panel.classList.toggle('cart-drawer__empty-content--hidden', !shouldShow);
    }
    if (panel.classList.contains('checkout-buttons')) {
      panel.classList.toggle('hidden', !shouldShow);
    }
  }

  getCartItemCount(cartForm, mergeItems) {
    const datasetCount = cartForm && cartForm.dataset.cartItemCount !== undefined
      ? parseInt(cartForm.dataset.cartItemCount, 10)
      : NaN;

    if (!Number.isNaN(datasetCount) && datasetCount >= 0) return datasetCount;
    return mergeItems.length;
  }

  recoverMissingFilledContent(cartForm, itemCount, mergeItems) {
    if (!cartForm || itemCount <= 0 || mergeItems.length > 0) {
      if (cartForm) delete cartForm.dataset.nsRecoveryRefresh;
      return;
    }

    if (cartForm.dataset.nsRecoveryRefresh === 'true') return;
    if (cartForm.classList.contains('cart-form--refreshing')) return;
    if (typeof cartForm.refresh !== 'function') return;

    cartForm.dataset.nsRecoveryRefresh = 'true';
    cartForm.refresh();
  }

  normalizeCartState() {
    const cartForm = this.querySelector('cart-form');
    const content = cartForm && cartForm.querySelector('.cart-drawer__content');
    const footer = cartForm && cartForm.querySelector('.cart-drawer__footer');
    const empty = cartForm && cartForm.querySelector('.cart-drawer__empty-content');
    const checkout = cartForm && cartForm.querySelector('.checkout-buttons');
    const mergeItems = cartForm
      ? cartForm.querySelectorAll('.cart-item-list__body [data-merge-list-item]:not(.merge-remove-item)')
      : [];
    const itemCount = this.getCartItemCount(cartForm, mergeItems);
    const hasItems = itemCount > 0;
    const state = hasItems ? 'filled' : 'empty';

    this.dataset.cartState = state;
    this.dataset.cartItemCount = String(Math.max(0, itemCount));

    if (cartForm) {
      cartForm.dataset.cartState = state;
      cartForm.dataset.cartItemCount = String(Math.max(0, itemCount));
    }

    this.setPanelVisibility(content, hasItems);
    this.setPanelVisibility(footer, hasItems);
    this.setPanelVisibility(checkout, hasItems);
    this.setPanelVisibility(empty, !hasItems);
    this.recoverMissingFilledContent(cartForm, itemCount, mergeItems);
  }

  bindEvents() {
    this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
    this.closeDrawerViaEventHandler = this.close.bind(this, null);
    this.cartRefreshHandler = this.handleCartRefresh.bind(this);
    document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    this.addEventListener('on:cart-drawer:before-open', () => {
      this.normalizeCartState();
      document.documentElement.classList.add('ns-cart-drawer-open');
      theme.manuallyLoadImages(this);
    });
    this.addEventListener('on:cart-drawer:after-open', () => {
      window.requestAnimationFrame(() => {
        this.normalizeCartState();
        this.initCrossSells();
      });
    });
    this.addEventListener('on:cart-drawer:after-close', () => {
      document.documentElement.classList.remove('ns-cart-drawer-open');
    });
    this.addEventListener('on:cart:after-merge', () => {
      this.normalizeCartState();
      theme.manuallyLoadImages(this);
      window.requestAnimationFrame(() => {
        this.normalizeCartState();
        this.initCrossSells();
      });
    });
  }

  disconnectedCallback() {
    if (this.cartRefreshHandler) {
      document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    }
    if (this.openDrawerViaEventHandler) {
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    }
    if (this.closeDrawerViaEventHandler) {
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    }
    this.boundCartDrawerEvents = false;
  }

  /**
   * Handle when the drawer is opened via an event
   * @param {object} evt - Event object.
   */
  handleDrawerOpenViaEvent(evt) {
    this.open(evt.detail ? evt.detail.opener : null, this.getFocusTarget());
  }

  open(opener, elementToFocus, callback) {
    this.normalizeCartState();
    super.open(opener, elementToFocus || this.getFocusTarget(), callback);
  }

  /**
   * Trigger refresh of contents
   */
  handleCartRefresh() {
    const cartForm = this.querySelector('cart-form');
    if (cartForm) cartForm.refresh();
  }

  /**
   * Update section's cart-form element with new contents
   * @param {string} html - Whole-section HTML.
   */
  updateFromCartChange(html) {
    const cartForm = this.querySelector('cart-form');
    if (cartForm) cartForm.refreshFromHtml(html);
    this.normalizeCartState();
  }
}

window.customElements.define('cart-drawer', CartDrawer);
